import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  updateDoc,
} from 'firebase/firestore';
import { db } from './config';
import { getAuthToken } from './auth-helpers';
import api from '../api/api';

// ============================================
// LEDGER
// ============================================

export interface LedgerEntry {
  id: string;
  agentUserId: string;
  invoiceId?: string;
  withdrawId?: string;
  type: 'GROSS' | 'FEE' | 'PAYOUT' | 'ADJUSTMENT';
  amountStars: number;
  /** Paiement reçu sur le bot personnel (formule Pro) : hors solde plateforme. */
  direct?: boolean;
  createdAt: Date;
  invoice?: {
    id: string;
    title?: string;
    description?: string;
    starsAmount: number;
  };
  withdraw?: {
    id: string;
    amountStars: number;
    status: string;
  };
  payerTelegramUserId?: string;
  payerUsername?: string;
  payerName?: string;
}

/** @deprecated Use ledgerApi.getBalance() — backend is the source of truth. */
export const getBalance = async (agentUserId: string): Promise<{
  total: number;
  available: number;
  pending: number;
}> => {
  const ledgerRef = collection(db, 'ledger_entries');
  const ledgerQuery = query(
    ledgerRef,
    where('agentUserId', '==', agentUserId)
  );
  
  const snapshot = await getDocs(ledgerQuery);
  let total = 0;
  for (const docSnap of snapshot.docs) {
    total += docSnap.data().amountStars || 0;
  }

  const withdrawsRef = collection(db, 'withdraw_requests');
  const statuses = ['REQUESTED', 'APPROVED', 'PROCESSING'];
  
  let pending = 0;
  for (const status of statuses) {
    const pendingQuery = query(
      withdrawsRef,
      where('agentUserId', '==', agentUserId),
      where('status', '==', status)
    );
    const pendingSnapshot = await getDocs(pendingQuery);
    for (const docSnap of pendingSnapshot.docs) {
      pending += docSnap.data().amountStars || 0;
    }
  }

  return {
    total,
    available: total - pending,
    pending,
  };
};

export const getLifetimeEarned = async (agentUserId: string): Promise<number> => {
  const ledgerRef = collection(db, 'ledger_entries');
  const grossQuery = query(
    ledgerRef,
    where('agentUserId', '==', agentUserId),
    where('type', '==', 'GROSS')
  );

  const snapshot = await getDocs(grossQuery);
  let lifetimeEarned = 0;
  for (const docSnap of snapshot.docs) {
    lifetimeEarned += docSnap.data().amountStars || 0;
  }

  return lifetimeEarned;
};

export const getLedgerEntries = async (
  agentUserId: string,
  cursor?: string,
  limitCount: number = 20
): Promise<LedgerEntry[]> => {
  const ledgerRef = collection(db, 'ledger_entries');
  let q = query(
    ledgerRef,
    where('agentUserId', '==', agentUserId),
    orderBy('createdAt', 'desc'),
    limit(limitCount)
  );

  if (cursor) {
    const cursorDoc = await getDoc(doc(db, 'ledger_entries', cursor));
    if (cursorDoc.exists()) {
      q = query(
        ledgerRef,
        where('agentUserId', '==', agentUserId),
        orderBy('createdAt', 'desc'),
        startAfter(cursorDoc),
        limit(limitCount)
      );
    }
  }

  const snapshot = await getDocs(q);
  const entries: LedgerEntry[] = [];

  for (const docSnap of snapshot.docs) {
    const data = docSnap.data();
    
    let invoice = undefined;
    if (data.invoiceId) {
      const invoiceDoc = await getDoc(doc(db, 'invoices', data.invoiceId));
      if (invoiceDoc.exists()) {
        const invoiceData = invoiceDoc.data();
        invoice = {
          id: invoiceDoc.id,
          title: invoiceData.title,
          description: invoiceData.description,
          starsAmount: invoiceData.starsAmount,
        };
      }
    }

    let withdraw = undefined;
    if (data.withdrawId) {
      const withdrawDoc = await getDoc(doc(db, 'withdraw_requests', data.withdrawId));
      if (withdrawDoc.exists()) {
        const withdrawData = withdrawDoc.data();
        withdraw = {
          id: withdrawDoc.id,
          amountStars: withdrawData.amountStars,
          status: withdrawData.status,
        };
      }
    }

    entries.push({
      id: docSnap.id,
      agentUserId: data.agentUserId,
      invoiceId: data.invoiceId,
      withdrawId: data.withdrawId,
      type: data.type,
      amountStars: data.amountStars,
      direct: data.direct === true,
      createdAt: data.createdAt?.toDate() || new Date(),
      invoice,
      withdraw,
      payerTelegramUserId: data.payerTelegramUserId,
      payerUsername: data.payerUsername,
      payerName: data.payerName,
    });
  }

  return entries;
};

// ============================================
// INVOICES
// ============================================

export type InvoiceType = 'SINGLE' | 'MULTIPLE' | 'CONTENT' | 'SUBSCRIPTION';
export interface InvoiceDelivery { type: 'FILE' | 'PHOTO' | 'VIDEO' | 'AUDIO' | 'LINK' | 'MESSAGE' | 'CHANNEL'; value: string; message?: string; }
export interface Invoice {
  id: string;
  agentUserId: string;
  inputMode: 'STARS' | 'EUR' | 'USD';
  inputAmount: number;
  starsAmount: number;
  feeAmount: number;
  netAmount: number;
  rateSnapshot: any;
  title?: string;
  description?: string;
  singleUse?: boolean;
  invoiceType?: InvoiceType;
  delivery?: InvoiceDelivery;
  subscription?: { mode: 'AUTO' | 'MANUAL'; periodDays: number };
  paymentCount?: number;
  status: 'CREATED' | 'SENT' | 'PAID' | 'CANCELED' | 'EXPIRED';
  telegramInvoiceLink?: string;
  telegramPayload?: string;
  telegramChargeId?: string;
  paidAt?: Date;
  createdAt: Date;
}

export const getInvoices = async (
  agentUserId: string,
  cursor?: string,
  limitCount: number = 20
): Promise<Invoice[]> => {
  const invoicesRef = collection(db, 'invoices');
  let q = query(
    invoicesRef,
    where('agentUserId', '==', agentUserId),
    orderBy('createdAt', 'desc'),
    limit(limitCount)
  );

  if (cursor) {
    const cursorDoc = await getDoc(doc(db, 'invoices', cursor));
    if (cursorDoc.exists()) {
      q = query(
        invoicesRef,
        where('agentUserId', '==', agentUserId),
        orderBy('createdAt', 'desc'),
        startAfter(cursorDoc),
        limit(limitCount)
      );
    }
  }

  const snapshot = await getDocs(q);
  return snapshot.docs.map((docSnap) => {
    const data = docSnap.data();
    return {
      id: docSnap.id,
      ...data,
      createdAt: data.createdAt?.toDate() || new Date(),
      paidAt: data.paidAt?.toDate(),
    } as Invoice;
  });
};

export const getRecentInvoices = async (
  agentUserId: string,
  limitCount: number = 5
): Promise<Invoice[]> => {
  return getInvoices(agentUserId, undefined, limitCount);
};

// Get only paid invoices (purchases)
export const getPaidInvoices = async (
  agentUserId: string,
  limitCount: number = 10
): Promise<Invoice[]> => {
  const invoicesRef = collection(db, 'invoices');
  const q = query(
    invoicesRef,
    where('agentUserId', '==', agentUserId),
    where('status', '==', 'PAID'),
    orderBy('paidAt', 'desc'),
    limit(limitCount)
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((docSnap) => {
    const data = docSnap.data();
    return {
      id: docSnap.id,
      ...data,
      createdAt: data.createdAt?.toDate() || new Date(),
      paidAt: data.paidAt?.toDate(),
    } as Invoice;
  });
};

// Créer une invoice (via backend car nécessite le bot Telegram)
export const createInvoice = async (invoiceData: {
  inputMode: 'STARS' | 'EUR' | 'USD';
  amount: number;
  title?: string;
  description?: string;
  singleUse?: boolean;
  invoiceType?: InvoiceType;
  delivery?: InvoiceDelivery;
  subscription?: { mode: 'AUTO' | 'MANUAL'; periodDays: number };
  paymentCount?: number;
}) => {
  try {
    const response = await api.post('/invoices', invoiceData);
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 401) {
      throw new Error('You must be logged in to create an invoice');
    }
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error(error.message || 'Error creating invoice');
  }
};

// ============================================
// WITHDRAWS
// ============================================

export interface WithdrawRequest {
  id: string;
  agentUserId: string;
  amountStars: number;
  destination: {
    type: string;
    address: string;
  };
  status: 'REQUESTED' | 'APPROVED' | 'PROCESSING' | 'PAID' | 'REJECTED' | 'FAILED';
  requestedAt: Date;
  processedAt?: Date;
  externalRef?: string;
  error?: string;
  rejectionReason?: string;
}

export const getWithdraws = async (
  agentUserId: string,
  cursor?: string,
  limitCount: number = 20
): Promise<WithdrawRequest[]> => {
  const withdrawsRef = collection(db, 'withdraw_requests');
  let q = query(
    withdrawsRef,
    where('agentUserId', '==', agentUserId),
    orderBy('requestedAt', 'desc'),
    limit(limitCount)
  );

  if (cursor) {
    const cursorDoc = await getDoc(doc(db, 'withdraw_requests', cursor));
    if (cursorDoc.exists()) {
      q = query(
        withdrawsRef,
        where('agentUserId', '==', agentUserId),
        orderBy('requestedAt', 'desc'),
        startAfter(cursorDoc),
        limit(limitCount)
      );
    }
  }

  const snapshot = await getDocs(q);
  return snapshot.docs.map((docSnap) => {
    const data = docSnap.data();
    return {
      id: docSnap.id,
      ...data,
      requestedAt: data.requestedAt?.toDate() || new Date(),
      processedAt: data.processedAt?.toDate(),
    } as WithdrawRequest;
  });
};

// Créer une demande de retrait (via backend pour validation)
export const createWithdrawRequest = async (withdrawData: {
  amountStars: number;
  tonAddress?: string;
}) => {
  const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3000'}/withdraws`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${await getAuthToken()}`,
    },
    body: JSON.stringify(withdrawData),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to create withdraw request');
  }

  return response.json();
};

// Mettre à jour l'adresse TON
export const updatePayoutDestination = async (userId: string, tonAddress: string) => {
  if (!userId) throw new Error('User ID required');

  const agentQuery = query(
    collection(db, 'agents'),
    where('userId', '==', userId),
    limit(1)
  );
  const agentSnapshot = await getDocs(agentQuery);

  if (agentSnapshot.empty) {
    throw new Error('Agent not found');
  }

  const agentDoc = agentSnapshot.docs[0];
  const agentRef = doc(db, 'agents', agentDoc.id);
  await updateDoc(agentRef, {
    tonAddress,
  });

  return { tonAddress };
};

// ============================================
// ADMIN WITHDRAWS
// ============================================

export const getAdminWithdraws = async (
  status?: string,
  limitCount: number = 50
): Promise<WithdrawRequest[]> => {
  const withdrawsRef = collection(db, 'withdraw_requests');
  let q;

  if (status) {
    q = query(
      withdrawsRef,
      where('status', '==', status),
      orderBy('requestedAt', 'desc'),
      limit(limitCount)
    );
  } else {
    q = query(
      withdrawsRef,
      orderBy('requestedAt', 'desc'),
      limit(limitCount)
    );
  }

  const snapshot = await getDocs(q);
  const withdraws: WithdrawRequest[] = [];

  for (const docSnap of snapshot.docs) {
    const data = docSnap.data();

    // Get agent user info
    const userDoc = await getDoc(doc(db, 'users', data.agentUserId));
    
    let agentUser: any = null;
    if (userDoc.exists()) {
      const userData = userDoc.data();
      
      // Get agent info
      const agentQuery = query(
        collection(db, 'agents'),
        where('userId', '==', userDoc.id),
        limit(1)
      );
      const agentSnapshot = await getDocs(agentQuery);
      
      let agent = null;
      if (!agentSnapshot.empty) {
        const agentDoc = agentSnapshot.docs[0];
        const agentData = agentDoc.data();
        agent = {
          displayName: agentData.displayName,
          tonAddress: agentData.tonAddress,
        };
      }

      agentUser = {
        telegramUserId: userData.telegramUserId,
        agent,
      };
    }

    withdraws.push({
      id: docSnap.id,
      ...data,
      requestedAt: data.requestedAt?.toDate() || new Date(),
      processedAt: data.processedAt?.toDate(),
      agentUser,
    } as any);
  }

  return withdraws;
};

// getAuthToken est importé depuis auth-helpers.ts
